import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import "@/index.css";
import App from "@/App";
import { installApiBaseFetchShim } from "@/utils/installApiBaseFetchShim";
import { captureAttributionOnce } from "@/utils/signupAttribution";
import { captureOpenAIAdsAttribution } from "@/utils/openaiAdsAttribution";
import { ensureAnonymousSessionId } from '@/utils/anonymousSessionId';

captureAttributionOnce();
captureOpenAIAdsAttribution();
installApiBaseFetchShim();

// Seed the window-scoped session id so authStore can read it before useTracking.
if (typeof window !== 'undefined') {
  (window as { __ts_session_id?: string }).__ts_session_id = ensureAnonymousSessionId();
}

// Initialize Sentry for error tracking
if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: window.location.hostname.includes('preview') ? 'preview' : 'production',
    
    // Capture 100% of errors, 10% of transactions for performance
    tracesSampleRate: 0.1,
    
    // Filter out noisy errors (these are harmless/expected)
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      /ResizeObserver/,
      'Non-Error promise rejection',
      /Loading chunk \d+ failed/,
      // PostHog/rrweb cross-origin iframe errors (harmless)
      /Blocked a frame with origin.*from accessing a cross-origin frame/,
      /removeEventListener.*cross-origin/,
      /SecurityError.*cross-origin frame/,
      // Common browser extensions
      /chrome-extension/,
      /moz-extension/,
    ],
    
    // Add user context when available
    beforeSend(event) {
      // Skip PostHog/rrweb errors entirely
      if (event.exception?.values?.some(e => 
        e.value?.includes('rrweb') || 
        e.value?.includes('posthog') ||
        e.stacktrace?.frames?.some(f => f.filename?.includes('rrweb'))
      )) {
        return null;
      }
      
      // Add device info
      event.tags = {
        ...event.tags,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        isMobile: window.innerWidth < 768 ? 'yes' : 'no',
      };
      return event;
    },
  });
}

// Suppress PostHog/rrweb SecurityError from React dev overlay
window.addEventListener('error', (event) => {
  if (event.message?.includes('SecurityError') && 
      event.message?.includes('removeEventListener')) {
    event.preventDefault();
  }
});

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

if ('serviceWorker' in navigator && process.env.NODE_ENV !== 'production') {
  // Dev: a service worker left over from a prior production/local run keeps
  // serving the stale cached app on localhost (dev registers no SW of its own).
  // Proactively unregister any SW + clear its caches so dev always reflects the
  // latest build. Reload once if an active SW was actually controlling the page.
  navigator.serviceWorker.getRegistrations().then((regs) => {
    const hadActive = !!navigator.serviceWorker.controller;
    Promise.all(regs.map((r) => r.unregister())).then(() => {
      if (window.caches) {
        caches.keys().then((keys) => Promise.all(keys.map((k) => caches.delete(k)))).catch(() => {});
      }
      if (hadActive) window.location.reload();
    });
  }).catch(() => {});
}
